// account-context — FONTE ÚNICA do contexto de sync a partir da conta Swipe.
//
// O contexto de sync de um aparelho é (providerCode, usuário da fonte) no store: é ele
// que monta o escopo do local-db (`p:CODE::pf:ID`), a chave de perfis
// (`CODE:usuário`) e o que o puller/flusher precisam pra puxar e mandar. Até
// 10/10/2026 só o pareamento QR da TV o preenchia (sequência resolve → setProvider →
// api.login → setAuth, inline no `usePairingFlow`). A superfície MOBILE (login por
// e-mail/Google) nunca o preenchia: o `syncPull` saía cedo, o `savePosition` não
// gravava, o "Continuar" voltava vazio e os perfis eram só locais. Uma sequência num
// lugar só fecha a classe: o próximo caminho de login chama isto e herda o contexto.
//
// Duas confirmações, e a diferença é deliberada:
//  - `authenticate` (TV): `api.login` (sessão Xtream do aparelho) e `setAuth`. A ordem
//    dos sets é a de sempre; o `pairingFinalizing` do RootRoute depende dela.
//  - `context-only` (mobile): `setAccountContext`. Sem `api.login` (o mobile usa o
//    Bearer Swipe; o login Xtream abriria uma sessão de aparelho a mais no provedor) e
//    sem `isAuthed` (ver `setAccountContext` no store).

import { api, ApiError } from './api'
import { accountKey, type AccountSourceType, useSessionStore } from './store'
import { getSwipeUserId, reportSyncContextMissing } from './sync'

/** O mínimo que a fonte da conta precisa dizer pra virar contexto. */
export interface AccountSourceRef {
  sourceType: AccountSourceType
  /** O código do provedor; na fonte MANUAL, o EFÊMERO (`D{hash}`) que o servidor deriva. */
  providerCode: string
  xtreamUsername: string
}

export type AccountSourceConfirm<T extends AccountSourceRef> =
  | {
      kind: 'authenticate'
      /** O login Xtream do aparelho (TV: `api.login`). Roda entre `setProvider` e `setAuth`. */
      login: (source: T) => Promise<{ accountExpiresAt: number | null }>
    }
  | { kind: 'context-only' }

export type HydrateOutcome = 'hydrated' | 'cancelled'

/**
 * resolve → branding → setProvider → (login) → setAuth | setAccountContext.
 *
 * Erros do `resolve`/`getBranding`/`login` sobem pro chamador (a TV trata o
 * `preference_not_set` como a tela de "configure no celular"). `isCancelled` é
 * consultado nos mesmos dois pontos de sempre (depois do branding e depois do login):
 * cancelado, nada mais é gravado.
 */
export async function hydrateSourceFromAccount<T extends AccountSourceRef>(deps: {
  resolve: () => Promise<T>
  confirm: AccountSourceConfirm<T>
  isCancelled?: () => boolean
}): Promise<HydrateOutcome> {
  const cancelled = deps.isCancelled ?? (() => false)
  const source = await deps.resolve()
  const branding = await api.getBranding(source.providerCode)
  if (cancelled()) return 'cancelled'
  const store = useSessionStore.getState()
  // O DONO do contexto que vai ser gravado (ver `contextOwner` no store): a conta da
  // sessão AGORA. Gravado antes dos sets: se a sessão trocar no meio, a assinatura do
  // context-owner derruba o contexto inteiro, com dono e tudo.
  store.setContextOwner(getSwipeUserId())
  // O `sourceType` vem do PRÓPRIO resolve e é o que impede a fonte MANUAL de virar
  // "fonte por código" neste aparelho (o code aqui pode ser o EFÊMERO). `chosenHere:
  // false`: hidratar LÊ a fonte que a conta já tem; não é escolha feita neste aparelho
  // e não pode gerar escrita de volta.
  store.setProvider(source.providerCode, branding, { type: source.sourceType, chosenHere: false })
  if (deps.confirm.kind === 'authenticate') {
    const res = await deps.confirm.login(source)
    if (cancelled()) return 'cancelled'
    // setAuth liga isAuthed=true E desliga pairingFinalizing no MESMO set() (átomo).
    useSessionStore.getState().setAuth(source.xtreamUsername, res.accountExpiresAt)
  } else {
    useSessionStore.getState().setAccountContext(source.xtreamUsername)
  }
  return 'hydrated'
}

/** Desfecho da hidratação na superfície mobile. */
export type MobileHydrateOutcome = 'hydrated' | 'unchanged' | 'no-source' | 'failed' | 'cancelled'

/**
 * Lê a fonte da conta pelo caminho de MENOR privilégio: o `GET /preference` não traz
 * a senha Xtream e basta pra fonte por CÓDIGO. Só a fonte MANUAL (que lá vem com
 * `providerCode: null`, porque o code efêmero é interno) precisa do `resolve`, que é o
 * único que conhece o `D{hash}`; a senha que ele traz é descartada aqui e nunca chega ao
 * store nem ao disco.
 */
async function readAccountSource(): Promise<AccountSourceRef> {
  const pref = await api.getSwipePreference({ silentAuthFailure: true })
  if (pref.providerCode) {
    return { sourceType: pref.sourceType, providerCode: pref.providerCode, xtreamUsername: pref.xtreamUsername }
  }
  const r = await api.swipePreferenceResolveActive({ silentAuthFailure: true })
  return { sourceType: r.sourceType, providerCode: r.providerCode, xtreamUsername: r.xtreamUsername }
}

/**
 * Hidrata o contexto de sync na superfície MOBILE — no login (e-mail/Google), no boot
 * com sessão e depois de salvar a fonte. Nunca lança.
 *  - `unchanged`: o store já tem esta conta confirmada → nada é regravado (o perfil
 *    ativo fica, nenhum branding é pedido).
 *  - `no-source`: a conta não tem fonte (404 `preference_not_set` / `no_provider`).
 *  - `failed`: rede/servidor. O contexto que o store já tinha (de um boot anterior)
 *    segue valendo.
 * Os dois últimos deixam rastro `sync_context_missing`.
 */
export async function hydrateMobileSourceContext(
  opts: { isCancelled?: () => boolean } = {},
): Promise<MobileHydrateOutcome> {
  const cancelled = opts.isCancelled ?? (() => false)
  let source: AccountSourceRef
  try {
    source = await readAccountSource()
  } catch (err) {
    if (cancelled()) return 'cancelled'
    const noSource = err instanceof ApiError && (err.code === 'preference_not_set' || err.code === 'no_provider')
    reportSyncContextMissing(noSource ? 'no_source' : 'resolve_failed')
    return noSource ? 'no-source' : 'failed'
  }
  // A resposta chegou depois de um logout/troca de conta (cybersec P1, caminho c):
  // gravar agora devolveria a conta que saiu. Nada é gravado.
  if (cancelled()) return 'cancelled'
  const s = useSessionStore.getState()
  const same =
    s.mode === 'provider' &&
    s.providerCode === source.providerCode &&
    s.sourceType === source.sourceType &&
    s.username === source.xtreamUsername &&
    s.navScopeCode === source.providerCode &&
    s.contextOwner === getSwipeUserId() &&
    accountKey(s) !== null
  if (same) return 'unchanged'
  try {
    const out = await hydrateSourceFromAccount({
      resolve: () => Promise.resolve(source),
      confirm: { kind: 'context-only' },
      isCancelled: cancelled,
    })
    return out === 'cancelled' ? 'cancelled' : 'hydrated'
  } catch {
    reportSyncContextMissing('resolve_failed')
    return 'failed'
  }
}
